import { Box, Button, Tooltip, Typography } from "@mui/material";
import React from "react";
import { usePagesTranslation } from "../../../../pages/locales";
import { templateForGridAndLevel } from "../engine/slidingTemplateUtils";
import { difficultyToLevel } from "../lib/api";
import { useSlidingAppearance } from "../lib/AppearanceContext";
import IOSPlayButton from "./IOSPlayButton";
import HomePlayButton from "./HomePlayButton";
import {
    cardOptionLabelSx,
    cardOptionRowSx,
    mono,
    wireOptionSx,
} from "../styles/mainStyles";
import { CHALLENGE_GRID, DIFFICULTIES, QUICK_PLAY_GRID } from "../routes";

const GRID_OPTIONS = [
    { size: QUICK_PLAY_GRID, label: "4×4" },
    { size: CHALLENGE_GRID, label: "6×6" },
];

export const LEVEL_LABELS = { easy: "Easy", medium: "Med", hard: "Hard" };

function gridLabelForSize(gridSize) {
    const gs = Number(gridSize);
    if (gs === QUICK_PLAY_GRID) return "4×4";
    if (gs === CHALLENGE_GRID) return "6×6";
    if (Number.isFinite(gs) && gs > 0) return `${gs}×${gs}`;
    return "—";
}

export function gridSizeAvailable(templates, size) {
    return (Array.isArray(templates) ? templates : []).some(
        (t) => Number(t?.gridSize) === size && t?.id
    );
}

export function levelAvailable(templates, gridSize, difficulty) {
    const level = difficultyToLevel(difficulty);
    return Boolean(templateForGridAndLevel(templates, gridSize, level)?.id);
}

export function resolveSlidingTemplate(templates, gridSize, difficulty) {
    return templateForGridAndLevel(templates, gridSize, difficultyToLevel(difficulty));
}

/** Wireframe toggle: selected `( opt )`, unselected `[ opt ]`. */
export function WireOption({ label, selected, onClick, disabled, compact, tooltip }) {
    const text = selected ? `( ${label} )` : `[ ${label} ]`;
    const button = (
        <Button
            type="button"
            onClick={onClick}
            disabled={disabled}
            sx={wireOptionSx(selected, compact)}
            aria-pressed={selected}
            aria-label={tooltip && disabled ? `${label}. ${tooltip}` : undefined}
        >
            {text}
        </Button>
    );

    if (tooltip && disabled) {
        return (
            <Tooltip title={tooltip} arrow placement="top" enterTouchDelay={0} leaveTouchDelay={3000}>
                <Box component="span" sx={{ display: "inline-flex" }}>
                    {button}
                </Box>
            </Tooltip>
        );
    }

    return button;
}

function OptionRow({ rowLabel, children }) {
    return (
        <Box sx={cardOptionRowSx} role="group" aria-label={rowLabel}>
            <Typography component="span" sx={cardOptionLabelSx}>
                {rowLabel}:
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                {children}
            </Box>
        </Box>
    );
}

const infoLabelSx = {
    ...cardOptionLabelSx,
    minWidth: "3.25rem",
    letterSpacing: "0.14em",
};

/** In-progress my-puzzles: MODE line and Resume (same gold CTA as Solve Puzzle). */
export function WirePlayProgressInfo({ modeLine, onResume, playing }) {
    const { t } = usePagesTranslation();
    const { tokens } = useSlidingAppearance();

    return (
        <Box sx={{ mt: 1 }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 1,
                    mb: 0.25,
                    width: "100%",
                }}
            >
                <Typography
                    component="span"
                    sx={{
                        ...infoLabelSx,
                        color: tokens.textSubtle,
                    }}
                >
                    MODE:
                </Typography>
                <Typography
                    component="span"
                    sx={{
                        fontFamily: mono,
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        color: tokens.text,
                        flex: 1,
                    }}
                >
                    {modeLine || "—"}
                </Typography>
            </Box>
            <HomePlayButton
                onClick={onResume}
                disabled={playing}
                loading={playing}
                ariaLabel={t("native.myPuzzles.resume", { defaultValue: "Resume" })}
                label={t("native.myPuzzles.resume", { defaultValue: "Resume" })}
            />
        </Box>
    );
}

/** Read-only GRID / LEVEL display (completed my-puzzles cards). */
export function WirePlaySummary({ gridSize, difficulty }) {
    const gridText = gridLabelForSize(gridSize);
    const levelText = LEVEL_LABELS[difficulty] || "—";
    const valueSx = {
        fontFamily: mono,
        fontSize: "0.72rem",
        fontWeight: 700,
        color: "#3BA666",
    };

    return (
        <Box sx={{ mt: 1 }}>
            <OptionRow rowLabel="GRID">
                <Typography component="span" sx={valueSx}>
                    ( {gridText} )
                </Typography>
            </OptionRow>
            <OptionRow rowLabel="LEVEL">
                <Typography component="span" sx={valueSx}>
                    ( {levelText} )
                </Typography>
            </OptionRow>
        </Box>
    );
}

/**
 * GRID / LEVEL rows + spaced PLAY button (library cards).
 */
export default function WirePlayControls({
    templates,
    gridSize,
    difficulty,
    onGridChange,
    onDifficultyChange,
    onPlay,
    playing,
    disabled,
    hidePlayButton = false,
}) {
    const tpls = Array.isArray(templates) ? templates : [];
    const canPlay = levelAvailable(tpls, gridSize, difficulty);

    return (
        <Box sx={{ mt: 1 }}>
            <OptionRow rowLabel="GRID">
                {GRID_OPTIONS.map((opt) => {
                    const available = gridSizeAvailable(tpls, opt.size);
                    return (
                        <WireOption
                            key={opt.size}
                            label={opt.label}
                            selected={gridSize === opt.size}
                            disabled={disabled || playing || !available}
                            compact
                            onClick={() => onGridChange(opt.size)}
                        />
                    );
                })}
            </OptionRow>

            <OptionRow rowLabel="LEVEL">
                {DIFFICULTIES.map((d) => {
                    const available = levelAvailable(tpls, gridSize, d);
                    return (
                        <WireOption
                            key={d}
                            label={LEVEL_LABELS[d]}
                            selected={difficulty === d}
                            disabled={disabled || playing || !available}
                            compact
                            onClick={() => onDifficultyChange(d)}
                        />
                    );
                })}
            </OptionRow>

            {hidePlayButton ? null : (
                <IOSPlayButton
                    onClick={onPlay}
                    disabled={disabled || !canPlay}
                    loading={playing}
                    ariaLabel="Play puzzle"
                    sx={{ mt: 1.25 }}
                />
            )}
        </Box>
    );
}
